import { List, Map } from 'immutable';
import Enum from 'enum';

import { ISO_8601, extensionStatuses } from 'flow/utils';
import { contactNameSorter, sortStrings } from 'flow/utils/sorter-utils';

// The default "relative country" of the number formatting. If no other country
// code is supplied, numbers are formatted as being an international or national
// number relative to this country code.
// It is also the default country code guess when parsing phone numbers.
export const DEFAULT_COUNTRY_CODE = 'SE';

// Construct the filter index for the supplied ExtensionDTO,
// which is expected to contain a ContactDTO.
export const extractIndex = (
    extension,
    searchMobileKeywordsDepartment,
    searchFixedNumber,
) => {
    const contact = extension.get('contact');
    if (contact) {
        return extractContactIndex(
            contact,
            searchMobileKeywordsDepartment,
            searchFixedNumber,
        );
    } else {
        return '';
    }
};

const extractContactIndex = (
    contact,
    searchOnExtendedInformation,
    searchFixedNumber,
) => {
    const fullName = extractContactName(contact);

    if (!searchOnExtendedInformation) {
        return fullName;
    }

    const fixedNumber = searchFixedNumber
        ? contact.getIn(['fixed', 'nationalFormat'], '') +
          ' ' +
          contact.getIn(['fixed', 'number'], '')
        : '';

    // Extract the mobile number on both int'l and national formats as a space-separated String
    const mobileNumber =
        contact.getIn(['mobile', 'nationalFormat'], '') +
        ' ' +
        contact.getIn(['mobile', 'number'], '');

    const companyName = contact.get('companyName');

    return (
        fullName +
        ' ' +
        fixedNumber +
        ' ' +
        mobileNumber +
        ' ' +
        companyName +
        ' ' +
        contact.get('keywords', '') +
        ' ' +
        contact.get('department', '')
    ).toLowerCase();
};

const extractThirdPartyContactIndex = (thirdPartyContact) => {
    const fullName = extractContactName(thirdPartyContact);
    const phoneNumber =
        thirdPartyContact.getIn(['phoneNumber', 'nationalFormat'], '') +
        ' ' +
        thirdPartyContact.getIn(['phoneNumber', 'number'], '');

    return (fullName + ' ' + phoneNumber).toLowerCase();
};

export const getPhoneNumberFromContact = (contact) => {
    if (!contact) {
        return null;
    }
    return contact.getIn(['phoneNumber', 'number'], '');
};

export const getAlt1PhoneNumberFromContact = (contact) => {
    if (!contact) {
        return null;
    }
    return contact.getIn(['altTelephone1', 'number'], '');
};

export const getAlt2PhoneNumberFromContact = (contact) => {
    if (!contact) {
        return null;
    }
    return contact.getIn(['altTelephone2', 'number'], '');
};

// Find the extension for which the predicate function returns true. If not found, an empty Map is returned.
// Note: this is quite an expensive operation, resulting in a linear search. Use prudently!
export const findExtension = (getState, predicateFunc) => {
    const { extensions } = getState();
    return extensions.get('extensions').find(predicateFunc, null, Map());
};

// Construct filter index for each of the supplied extensions,
// and attempt to match it against the supplied filter text.
export const filterExtensionsByName = (extensions, filterText = '') => {
    const search = filterText.toLowerCase();

    return extensions.filter(
        (extension) =>
            extractIndex(extension, false, false).indexOf(search) !== -1,
    );
};

// Construct filter index for each of the supplied extensions,
// and attempt to match it against the supplied filter text.
export const filterExtensionsBySearchableInfo = (
    extensions,
    filterText = '',
    includeFixedNumber = false,
) => {
    const search = filterText.toLowerCase();

    return extensions.filter(
        (extension) =>
            extractIndex(extension, true, includeFixedNumber).indexOf(
                search,
            ) !== -1,
    );
};

export const filterContactsBySearchableInfo = (
    contacts,
    filterText = '',
    includeFixedNumber = false,
) => {
    const search = filterText.toLowerCase();

    return contacts.filter(
        (contact) =>
            extractContactIndex(contact, true, includeFixedNumber).indexOf(
                search,
            ) !== -1,
    );
};

export const filterThirdPartyContactsBySearchableInfo = (
    thirdPartyContacts,
    filterText = '',
) => {
    const search = filterText.toLowerCase();

    return thirdPartyContacts.filter(
        (thirdPartyContact) =>
            extractThirdPartyContactIndex(thirdPartyContact).indexOf(search) !==
            -1,
    );
};

// What the colleague list sorts and groups by. A change to the name rule
// changes these values, so the list sorts again.
export const extensionSortKey = (extension) =>
    Map({
        key: extension.get('key'),
        name: extractIndex(extension),
        // A missing or null department groups as '', which sorts safely.
        department: extension.getIn(['contact', 'department']) ?? '',
    });

export const sortExtensionsByName = (sortKeys) => {
    if (!sortKeys) {
        return List();
    }
    return sortKeys.sortBy((sortKey) => sortKey.get('name'), sortStrings);
};

export const getFixedNumberFromExtension = (extension) => {
    if (!extension) {
        return null;
    }
    return extension.getIn(['contact', 'fixedNumber']);
};

export const getMobileNumberFromExtension = (extension) => {
    if (!extension) {
        return null;
    }
    return extension.getIn(['contact', 'mobileNumber']);
};

export const getFixedNumberFromContact = (contact) => {
    if (!contact) {
        return null;
    }
    return contact.getIn(['fixed', 'number'], null);
};

export const getMobileNumberFromContact = (contact) => {
    if (!contact) {
        return null;
    }
    return contact.getIn(['mobile', 'number'], null);
};

export const getTechnicalNumberFromContact = (contact) => {
    if (!contact) {
        return null;
    }
    return contact.getIn(['technicalNumber', 'number'], null);
};

export const getFixedOrTechnicalOrMobileNumberFromContact = (contact) => {
    if (!contact) {
        return null;
    }
    return (
        contact.getIn(['fixed', 'number']) ||
        contact.getIn(['technicalNumber', 'number']) ||
        contact.getIn(['mobile', 'number'])
    );
};

export const getCallableNumberFromContact = (contact) => {
    return (
        getFixedNumberFromContact(contact) ||
        getTechnicalNumberFromContact(contact) ||
        getMobileNumberFromContact(contact) ||
        getPhoneNumberFromContact(contact)
    );
};

export const getAllCallableNumbersFromContact = (contact) => {
    if (!contact) {
        return [];
    }
    const allNumbers = [];
    allNumbers.push(getFixedNumberFromContact(contact));
    allNumbers.push(getTechnicalNumberFromContact(contact));
    allNumbers.push(getMobileNumberFromContact(contact));
    allNumbers.push(getPhoneNumberFromContact(contact));
    allNumbers.push(getAlt1PhoneNumberFromContact(contact));
    allNumbers.push(getAlt2PhoneNumberFromContact(contact));

    const allValidNumbers = allNumbers.filter(Boolean);
    return allValidNumbers;
};

export const getFixedOrTechnicalOrMobileNumberFromExtension = (extension) => {
    if (!extension) {
        return null;
    }

    return getFixedOrTechnicalOrMobileNumberFromContact(
        extension.get('contact'),
    );
};

// Construct a sorted list of ContactDTOs
export const sortContactsByName = (contacts) => {
    if (!contacts) {
        return List();
    }
    return contacts.sort(contactNameSorter);
};

export const extractContactName = (contact = Map()) => {
    return (
        contact.get('firstName', '') +
        ' ' +
        contact.get('lastName', '')
    ).toLowerCase();
};

export const getThirdPartyContactKey = (thirdPartyContact) => {
    const phoneNumber = thirdPartyContact.getIn([
        'phoneNumber',
        'nationalFormat',
    ]);

    return (
        thirdPartyContact.get('key') +
        (phoneNumber ? '-' + phoneNumber.replace(/\D/g, '') : '')
    );
};

// Extract values from a ThirdPartyContactDTO relevant for creating a new personal contact.
export const getContactMapFromThirdPartyContact = (thirdPartyContact) => {
    return Map()
        .set('firstName', thirdPartyContact.get('firstName'))
        .set('lastName', thirdPartyContact.get('lastName'))
        .set('email', thirdPartyContact.get('email'))
        .set('companyName', thirdPartyContact.get('companyName'))
        .set('address', thirdPartyContact.get('address'))
        .set('postalcode', thirdPartyContact.get('postalcode'))
        .set('city', thirdPartyContact.get('city'))
        .set('country', thirdPartyContact.get('country'))
        .set('fixed', thirdPartyContact.get('phoneNumber'));
};

export const isGoogleContact = (thirdPartyContact) => {
    return (
        thirdPartyContact && thirdPartyContact.get('contactSource') === 'google'
    );
};

export const isMicrosoftContact = (thirdPartyContact) => {
    return (
        thirdPartyContact &&
        thirdPartyContact.get('contactSource') === 'microsoft'
    );
};

export const isIntegrationContact = (thirdPartyContact) => {
    return thirdPartyContact && !!thirdPartyContact.get('contactSource');
};

export const isDynamicSourceContact = (thirdPartyContact) => {
    return thirdPartyContact && !!thirdPartyContact.get('sourceDescription');
};

// Produce a Immutable.Map, keyed by sorted department name, with values
// being Immutable.Lists of extensionSortKey values.
export const groupExtensionsBySortedDepartment = (
    sortKeys,
    priorityDepartment,
) => {
    return sortKeys
        .groupBy((sortKey) => sortKey.get('department'))
        .sort((departmentListA, departmentListB) => {
            const departmentNameA = departmentListA.first().get('department');
            const departmentNameB = departmentListB.first().get('department');
            if (
                departmentNameA === priorityDepartment ||
                departmentNameB === ''
            ) {
                return -1;
            } else if (
                departmentNameB === priorityDepartment ||
                departmentNameA === ''
            ) {
                return 1;
            } else {
                return departmentNameA.localeCompare(departmentNameB, 'sv');
            }
        });
};

/**
 * Naive method to validate a callable number.
 *
 * @return true if a number starts with (optional +) and contains only numbers.
 */
export const isCallableNumber = (number) => {
    return /(^\+?[0-9]+$)/.test(number);
};

export const isCallableNumberTW = (number) => {
    return /(^\+?[0-9*]+$)/.test(number);
};

/**
 * Check if a users mobile number is verified.
 *
 * @param {Map} mobileStatus
 */
export const mobileNumberIsVerified = (mobileStatus) => {
    if (!mobileStatus) {
        return false;
    }
    const status = mobileStatus.get('mobileNumberState');
    switch (status) {
        case NumberState.nonverified.key:
        case NumberState.numbermissing.key:
            return false;
        default:
            return true;
    }
};

/**
 * Method to extract a country code
 * We do not know what country code the number has, so we guess that it is the same as the users number
 * or the customer country code.
 *
 * @param contact, the logged in user
 * @param customer the
 * @returns {string}
 */
export const getCountryCode = (contact, customer) => {
    if (contact) {
        const fixedCountryCode = contact
            .getIn(['fixed', 'countryCode'], '')
            .toLowerCase();
        const mobileCountryCode = contact
            .getIn(['mobile', 'countryCode'], '')
            .toLowerCase();
        if (fixedCountryCode || mobileCountryCode) {
            return fixedCountryCode ? fixedCountryCode : mobileCountryCode;
        }
    }
    if (customer) {
        return customer.getIn(['country', 'countrycode'], DEFAULT_COUNTRY_CODE);
    }
    return DEFAULT_COUNTRY_CODE;
};

/**
 * Derive the sum of unread voiceMessages.
 *
 * @param voiceMessages
 * @return the total number of unread voiceMessages or 0.
 */
export const calcTotalUnreadVoiceMessages = (voiceMessages) => {
    if (voiceMessages) {
        return voiceMessages.reduce(
            (acc, voiceMessage) => acc + hasUnreadMessage(voiceMessage),
            0,
        );
    } else {
        return 0;
    }
};

const hasUnreadMessage = (voiceMessage) => {
    return voiceMessage.get('voiceMessageState') === 'unread' ? 1 : 0;
};

/**
 * Represents the different types a contact can be presented as, see ContactDTO
 * @type {*}
 */
export const ContactType = new Enum({
    bound: 'bound',
    shared: 'shared',
    personal: 'personal',
    global: 'global',
    hidden: 'hidden',
    undefined: 'undefined',
    unknown: 'unknown',
});

export const NumberState = new Enum([
    'msisdn',
    'verified',
    'nonverified',
    'numbermissing',
    'unknown',
]);

export const isContactType = (contactTypeString) => {
    if (typeof contactTypeString !== 'string') {
        return false;
    }
    return ContactType.enums.some((enumItem) => {
        return enumItem.key.toLowerCase() === contactTypeString.toLowerCase();
    });
};

export const getVoiceMessagesAfterTimestamp = (voiceMessages, timestamp) => {
    if (!List.isList(voiceMessages) && !Map.isMap(voiceMessages)) {
        return null;
    }
    return voiceMessages.filter(
        (voiceMessage) =>
            voiceMessage.get('datetime').substring(0, ISO_8601.length) >
            timestamp,
    );
};

export const extractIdFromExtentionKey = (extensionKey = '') => {
    const parsedResult = /^extension-(\d+)/.exec(extensionKey);
    return parsedResult ? parsedResult[1] : '';
};

export const extensionStatusIsInCallOrInExternalCall = (status) =>
    status === extensionStatuses.inCall.key ||
    status === extensionStatuses.inExternalCall.key;
